// ============================================================================
// BACKEND API CLIENT
// Handles auth tokens, auto-refresh, and API calls to the Express server
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

let accessToken = null;
let refreshToken = null;

// Load tokens from sessionStorage on init
try {
  const stored = sessionStorage.getItem('__fc_tokens');
  if (stored) {
    const t = JSON.parse(stored);
    accessToken = t.accessToken;
    refreshToken = t.refreshToken;
  }
} catch { /* */ }

function saveTokens(access, refresh) {
  accessToken = access;
  refreshToken = refresh;
  try {
    sessionStorage.setItem('__fc_tokens', JSON.stringify({ accessToken: access, refreshToken: refresh }));
  } catch { /* */ }
}

function clearTokens() {
  accessToken = null;
  refreshToken = null;
  try { sessionStorage.removeItem('__fc_tokens'); } catch { /* */ }
}

// Core fetch with auth headers
async function apiFetch(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  let res = await fetch(url, { ...options, headers });

  // If 401 with expired token, try refresh
  if (res.status === 401 && refreshToken) {
    const body = await res.json().catch(() => ({}));
    if (body.code === 'TOKEN_EXPIRED') {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        res = await fetch(url, { ...options, headers });
      }
    }
  }

  return res;
}

async function tryRefreshToken() {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (res.ok) {
      const data = await res.json();
      saveTokens(data.accessToken, data.refreshToken);
      return true;
    }
  } catch { /* */ }
  clearTokens();
  return false;
}

// ── Auth API ──

export async function apiLogin(email, password) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Login failed');
  saveTokens(data.accessToken, data.refreshToken);
  return data.user;
}

export async function apiSignup(name, email, password, { phone, country, bio } = {}) {
  const res = await fetch(`${API_BASE}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password, phone, country, bio }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Signup failed');
  return data;
}

// Both OAuth flows POST the provider's id_token to the backend, which
// verifies it, finds or creates the matching user, and either issues our
// own JWT (on success) or surfaces the same PENDING_APPROVAL / TRIAL_EXPIRED
// codes the email/password login uses. Server-issued tokens are saved
// locally so the rest of the app behaves exactly as if the user logged in
// with email + password.
export async function apiGoogleLogin(idToken) {
  const res = await fetch(`${API_BASE}/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Google sign-in failed');
    err.code = data.code;
    throw err;
  }
  saveTokens(data.accessToken, data.refreshToken);
  return data.user;
}

export async function apiAppleLogin(idToken, applePayload = null) {
  const res = await fetch(`${API_BASE}/auth/apple`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, user: applePayload }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Apple sign-in failed');
    err.code = data.code;
    throw err;
  }
  saveTokens(data.accessToken, data.refreshToken);
  return data.user;
}

export async function apiGetMe() {
  const res = await apiFetch('/auth/me');
  if (!res.ok) return null;
  const data = await res.json();
  return data.user;
}

export async function apiUpdateProfile({ name, phone, country, bio }) {
  const res = await apiFetch('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify({ name, phone, country, bio }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update profile');
  return data.user;
}

export async function apiDismissTour() {
  const res = await apiFetch('/auth/dismiss-tour', { method: 'POST' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to dismiss tour');
  return data.user;
}

export async function apiGetSignalPerformance(days = 30) {
  const res = await apiFetch(`/signals/performance?days=${days}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load performance');
  return data;
}

// Per-(cluster, direction) bucket data for signal-engine calibration.
// Empty buckets array on first deploy is expected (walk-forward not yet run);
// scorer falls back to raw confidence in that case.
export async function apiGetCalibration(days = 30) {
  try {
    const res = await apiFetch(`/signals/calibration?days=${days}`);
    if (!res.ok) return { days, buckets: [] };
    return await res.json();
  } catch {
    return { days, buckets: [] };
  }
}

export async function apiLogout() {
  try { await apiFetch('/auth/logout', { method: 'POST' }); } catch { /* */ }
  clearTokens();
}

export async function apiForgotPassword(email) {
  const res = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to process request');
  return data;
}

export async function apiResetPassword(token, password) {
  const res = await fetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to reset password');
  return data;
}

export function hasStoredTokens() {
  return !!accessToken;
}

// Used by market data services to attach the JWT to backend proxy requests
export function getAuthHeaders() {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

// ── Admin API ──

export async function apiGetAllUsers() {
  const res = await apiFetch('/admin/users');
  if (!res.ok) throw new Error('Failed to fetch users');
  const data = await res.json();
  return data.users;
}

export async function apiGetPendingUsers() {
  const res = await apiFetch('/admin/pending');
  if (!res.ok) throw new Error('Failed to fetch pending users');
  return (await res.json());
}

export async function apiApproveUser(userId) {
  const res = await apiFetch(`/admin/approve/${userId}`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to approve user');
  return data;
}

export async function apiRejectUser(userId) {
  const res = await apiFetch(`/admin/reject/${userId}`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to reject user');
  return data;
}

export async function apiExtendSubscription(userId, { days, forever } = {}) {
  const res = await apiFetch(`/admin/subscription/${userId}/extend`, {
    method: 'POST',
    body: JSON.stringify({ days, forever }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to extend subscription');
  return data;
}

export async function apiPauseSubscription(userId) {
  const res = await apiFetch(`/admin/subscription/${userId}/pause`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to pause subscription');
  return data;
}

export async function apiResumeSubscription(userId) {
  const res = await apiFetch(`/admin/subscription/${userId}/resume`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to resume subscription');
  return data;
}

export async function apiCancelSubscription(userId) {
  const res = await apiFetch(`/admin/subscription/${userId}/cancel`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to cancel subscription');
  return data;
}

export async function apiDeleteUser(userId) {
  const res = await apiFetch(`/admin/users/${userId}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to delete user');
  return data;
}

// ── Favorites API ──

export async function apiGetFavorites() {
  const res = await apiFetch('/favorites');
  if (!res.ok) return [];
  const data = await res.json();
  return data.favorites || [];
}

export async function apiAddFavorite(ticker, name) {
  const res = await apiFetch('/favorites', {
    method: 'POST',
    body: JSON.stringify({ ticker, name }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to add favorite');
  }
  return (await res.json());
}

export async function apiRemoveFavorite(ticker) {
  const res = await apiFetch(`/favorites/${ticker}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to remove favorite');
  return (await res.json());
}

// ── Trading API ──

export async function apiGetPortfolio() {
  const res = await apiFetch('/trading/portfolio');
  if (!res.ok) throw new Error('Failed to load portfolio');
  return (await res.json());
}

// T4.2: portfolio heat + sector exposure + warnings. Defensive: returns null
// on any error so a slow/failing heat endpoint never blocks the Trading page
// from rendering positions.
export async function apiGetPortfolioHeat() {
  try {
    const res = await apiFetch('/trading/heat');
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function apiExecuteTrade(tradeData) {
  const res = await apiFetch('/trading/execute', {
    method: 'POST',
    body: JSON.stringify(tradeData),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Trade failed');
  return data;
}

export async function apiCloseTrade(tradeId, closePrice) {
  const res = await apiFetch(`/trading/close/${tradeId}`, {
    method: 'POST',
    body: JSON.stringify({ closePrice }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to close trade');
  return data;
}

export async function apiGetTrades() {
  const res = await apiFetch('/trading/trades');
  if (!res.ok) throw new Error('Failed to load trades');
  return (await res.json());
}

export async function apiGetPortfolioHistory(days = 30) {
  const res = await apiFetch(`/trading/history?days=${days}`);
  if (!res.ok) return { history: [] };
  return (await res.json());
}

// ── Admin Balance API ──

export async function apiGetUserBalances() {
  const res = await apiFetch('/admin/balances');
  if (!res.ok) throw new Error('Failed to load balances');
  return (await res.json());
}

export async function apiSetUserBalance(userId, amount) {
  const res = await apiFetch(`/admin/set-balance/${userId}`, {
    method: 'POST',
    body: JSON.stringify({ amount }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to set balance');
  return data;
}

export async function apiTriggerDigest() {
  const res = await apiFetch(`/admin/trigger-digest`, { method: 'POST' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to trigger digest');
  return data;
}

export async function apiGetCacheStats() {
  const res = await apiFetch('/admin/cache-stats');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load cache stats');
  return data;
}

// Fetches the raw Polygon /v3/snapshot/options/{underlying}/{contract} payload
// for a single contract, returning both a flat summary and the full body.
// Used by the AdminPage diagnostic to verify "no live bid/ask" cases.
export async function apiOptionQuoteDebug(underlying, contract) {
  const u = encodeURIComponent(underlying);
  const c = encodeURIComponent(contract);
  const res = await apiFetch(`/admin/option-quote-debug?underlying=${u}&contract=${c}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Current VIX level + classified regime + active gate floors.
export async function apiGetVixRegime() {
  const res = await apiFetch('/admin/vix-regime');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── FINRA short-volume admin endpoints (Boehmer-Jones-Zhang 2008 signal) ──
// History: GET /api/admin/short-flow?ticker=AAPL → { ticker, latestDate, count, rows }
// Trigger: POST /api/admin/short-flow/run → { message, latestDate }
export async function apiGetShortFlow(ticker = '') {
  const q = ticker ? `?ticker=${encodeURIComponent(String(ticker).toUpperCase())}` : '';
  const res = await apiFetch(`/admin/short-flow${q}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function apiRunShortFlowJob() {
  const res = await apiFetch(`/admin/short-flow/run`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── SEC Form 4 insider-flow admin endpoints (Cohen-Malloy-Pomorski signal) ──
// History: GET /api/admin/insider-flow?ticker=AAPL → { ticker, latestFilingDate, count, rows }
// Trigger: POST /api/admin/insider-flow/run → { message, latestFilingDate }
export async function apiGetInsiderFlow(ticker = '') {
  const q = ticker ? `?ticker=${encodeURIComponent(String(ticker).toUpperCase())}` : '';
  const res = await apiFetch(`/admin/insider-flow${q}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function apiRunInsiderFlowJob() {
  const res = await apiFetch(`/admin/insider-flow/run`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Open tracked signals (Track Record live positions) ──
// GET /api/admin/tracked-signals?shippedOnly=…&limit=… →
//   { count, priced, avgReturnPct, signals: [...] }
// Each signal carries its live directional return, status (open / at target /
// at stop), age, and time remaining in its swing window.
export async function apiGetTrackedSignals({ shippedOnly = false, limit = 200 } = {}) {
  const params = new URLSearchParams();
  if (shippedOnly) params.set('shippedOnly', 'true');
  params.set('limit', String(limit));
  const res = await apiFetch(`/admin/tracked-signals?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Non-overlapping weekly win-rate + return trend, so a real over-time
// direction is visible (vs the nested 15/30/90-day windows). Returns
// { weeks, buckets, trend: [{ weekStart, winRatePct, avgReturnPct, scored, … }] }.
export async function apiGetPerformanceTrend({ weeks = 12 } = {}) {
  const res = await apiFetch(`/admin/performance-trend?weeks=${weeks}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Per-factor attribution (finer-grained self-learning) ──
// GET  /admin/factor-attribution      → { applied, meta, factors: [...] }
// POST /admin/factor-attribution/run  → recompute now, same shape
// `applied` echoes whether the learned nudge is live (FACTOR_ADAPTIVE_ENABLED);
// `meta` maps factor key → { label, cluster }; `factors` is the track record.
export async function apiGetFactorAttribution() {
  const res = await apiFetch('/admin/factor-attribution');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function apiRunFactorAttribution() {
  const res = await apiFetch('/admin/factor-attribution/run', { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Lists the contracts Polygon has for an underlying — used to verify whether
// a stored OCC symbol actually exists in Polygon's contract universe.
export async function apiOptionContractsList(underlying, limit = 200) {
  const u = encodeURIComponent(underlying);
  const res = await apiFetch(`/admin/option-contracts-list?underlying=${u}&limit=${limit}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── T2.1: outcome-flywheel health (admin) ──
// Shows total / scored / pending / shipped-vs-shadow split per exchange.
// Lets the operator confirm the digest is persisting shadow rows and the
// scorer is keeping up with the new volume.
export async function apiGetOutcomesHealth() {
  const res = await apiFetch('/admin/signal-outcomes-health');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── T1.1: confidence reliability curve (admin) ──
export async function apiGetCalibrationCurve() {
  const res = await apiFetch('/admin/calibration-curve');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function apiRunCalibrationCurve() {
  const res = await apiFetch('/admin/calibration-curve/run', { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── T1.3: public client read of the curve ──
// Used by the /signals page to remap displayed confidence to historically
// observed WR. Empty array on first deploy / curve never built — applyCurve
// falls back to identity. Defensive against errors: returns [] so a slow or
// failing endpoint never blocks page render.
// Memoised at module level — the curve doesn't change during a session and
// every page that runs validateSignalsBatch needs it BEFORE callbacks fire,
// otherwise fast-cached backtest results land while curveRef is still []
// and applyCalibrationCurve falls back to identity (the heuristic value
// instead of the observed-WR mapping). Sharing one Promise also coalesces
// the request across pages.
let _curvePromise = null;
export async function apiGetCalibrationCurvePublic() {
  if (_curvePromise) return _curvePromise;
  _curvePromise = (async () => {
    try {
      const res = await apiFetch('/signals/calibration-curve');
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data?.curve) ? data.curve : [];
    } catch {
      return [];
    }
  })();
  // On failure of the underlying fetch we want a retry on the next call,
  // not a stuck-forever rejection. Reset the cache if the promise resolves
  // to an empty array (the catch-all error path).
  _curvePromise.then(curve => { if (!curve.length) _curvePromise = null; });
  return _curvePromise;
}

// ── AI Signal Evaluation ──

// ── Macro / market context ──

// Latest VIX close from Polygon. Returns { vix: number|null }.
// Hits the dedicated server-side getVixLevel helper rather than
// proxying I:VIX through the generic /api/market/polygon path — index
// tickers have different entitlement / latency than equities and the
// dedicated route hides that variance.
export async function apiGetVix() {
  try {
    const res = await apiFetch('/market/vix');
    if (!res.ok) return { vix: null };
    return await res.json();
  } catch {
    return { vix: null };
  }
}

// Sends current signals to Groq for context-aware confidence adjustments + a summary.
// signals: array of { ticker, direction, confidence, changePct, reasons }
export async function apiEvaluateSignals(signals) {
  const res = await apiFetch('/ai/evaluate', {
    method: 'POST',
    body: JSON.stringify({ signals }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Server returns { error: 'AI analysis unavailable', detail: '<real reason>' }.
    // Surface the detail so the UI shows the actual cause (Groq timeout, missing key,
    // rate limit, etc.) instead of an opaque generic banner.
    throw new Error(data.detail || data.error || 'AI analysis failed');
  }
  return data; // { adjustments: [{ticker, adjustment, reasoning}], summary }
}
